import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, State } from '@stencil/core';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { VISIBLE_DOTS, VISIBLE_SIDE_DOTS, VISIBLE_SIDE_DOTS_WITH_ACTIVE, MOVE_DURATION } from './dot-navigation.constants';
import { DotElement } from './dot-element/dot-element';

/**
 * The Dot Navigation is used to switch between multiple content pages, providing an overview of the available content.
 */
@Component({
  tag: 'wb-dot-navigation',
  styleUrl: 'dot-navigation.scss',
  shadow: true,
})
export class DotNavigationComponent implements ComponentInterface {
  @Element()
  public el!: HTMLElement;

  /** Number of available pages */
  @Prop({ reflect: true })
  public pages: number = 5;

  /** Currently active page (starts with 1 for first page) */
  @Prop({ reflect: true, mutable: true })
  public currentPage: number = 1;

  /**
   * Colour scheme
   * @deprecated Use `data-on` attribute instead
   * */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme;

  /**
   * If present adds a contrast shape on the background
   * @deprecated Use `has-background` attribute instead
   * */
  @Prop({ reflect: true })
  public contrast: boolean = false;

  /** If present adds a background */
  @Prop({ reflect: true })
  public hasBackground: boolean = false;

  /** If present, prevents interactions, meaning `currentPage` can't be changed by clicking */
  @Prop({ reflect: true })
  public preventInteraction: boolean = false;

  /** If present, prevents dot change when clicked, ideal for handling with parent component (e.g. slider) */
  @Prop({ reflect: true })
  public preventDotClickChange: boolean = false;

  /** Sets the accessible label for the dot navigation container, describing its purpose for assistive technologies. */
  @Prop({ reflect: true })
  public dotNavigationAriaLabel: string = 'This is a dot navigation that allows you to navigate to each element by using the arrow keys on your keyboard';

  /**
   * Sets the accessible label for each dot navigation element, describing its purpose for assistive technologies.
   * The placeholder :page is automatically replaced by the page number, :size indicates the total number of pages.
   */
  @Prop({ reflect: true })
  public dotElementAriaLabel: string = 'Page :page of :size';

  /** Event emitted when current page changes */
  @Event()
  public wbchange!: EventEmitter<number>;

  @State()
  private isMovingLeft: boolean = false;

  @State()
  private isMovingRight: boolean = false;

  /** goes to the previous available page */
  @Method()
  public previousPage(): Promise<void> {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.wbchange.emit(this.currentPage);
    }

    return Promise.resolve();
  }

  /** goes to the next available page */
  @Method()
  public nextPage(): Promise<void> {
    if (this.currentPage < this.pages) {
      this.currentPage++;
      this.wbchange.emit(this.currentPage);
    }

    return Promise.resolve();
  }

  @Listen('click')
  public handleClick(event: MouseEvent): void {
    if (this.preventInteraction) {
      return;
    }

    const clickedDotElement = event.composedPath().find((el: HTMLElement) => el?.classList?.contains('dot')) as HTMLElement;

    if (clickedDotElement) {
      const index = querySelectorAllAsArray<HTMLElement>(this.el.shadowRoot, '.dot').indexOf(clickedDotElement);
      const currentPage = index + 1;
      if (!this.preventDotClickChange) {
        this.currentPage = currentPage;
      }
      this.wbchange.emit(currentPage);
    }
  }

  /** Handle arrow keys on dots */
  @Listen('keydown')
  public handleKeyDown(event: KeyboardEvent): void {
    const target = event.composedPath()[0] as HTMLElement;

    if (!target.classList.contains('dot') || this.preventInteraction) {
      return;
    }

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.nextPage().then(() => this.focusActiveDot());
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.previousPage().then(() => this.focusActiveDot());
        break;
    }
  }

  public componentDidUpdate(): void {
    if (this.isMovingLeft || this.isMovingRight) {
      this.resetMoving();
    } else {
      const isLeft = this.currentPage < this.transitionPage;
      const moves = Math.abs(this.currentPage - this.transitionPage);

      if (moves > 0) {
        this.setTransitionPage(isLeft);
        this.setMoving(isLeft);
      }
    }
  }

  private async focusActiveDot(): Promise<void> {
    const dots = querySelectorAllAsArray<HTMLButtonElement>(this.el.shadowRoot, 'button.dot');
    const index = this.currentPage - 1;
    dots[index]?.focus();
  }

  private transitionPage: number = this.currentPage;

  private setTransitionPage(isLeft: boolean): void {
    this.transitionPage = isLeft ? this.transitionPage - 1 : this.transitionPage + 1;
  }

  private setMoving(isLeft: boolean): void {
    requestAnimationFrame(() => {
      this.isMovingLeft = isLeft;
      this.isMovingRight = !isLeft;
    });
  }

  private resetMoving(): void {
    setTimeout(() => {
      this.isMovingLeft = false;
      this.isMovingRight = false;
    }, MOVE_DURATION);
  }

  public render(): JSX.Element {
    const isInitialPages: boolean = this.transitionPage <= VISIBLE_SIDE_DOTS;
    const isLastPages: boolean = this.transitionPage > this.pages - VISIBLE_SIDE_DOTS;
    const initialDirection: boolean = this.transitionPage === VISIBLE_SIDE_DOTS_WITH_ACTIVE && this.isMovingLeft;
    const lastDirection: boolean = this.transitionPage === this.pages - VISIBLE_SIDE_DOTS && this.isMovingRight;
    const directionalTransitionDots: number = initialDirection || lastDirection ? VISIBLE_SIDE_DOTS : VISIBLE_SIDE_DOTS_WITH_ACTIVE;
    const isInnerDots: boolean = this.transitionPage > directionalTransitionDots && this.transitionPage <= this.pages - directionalTransitionDots;
    const hasMoreThanVisibleDots: boolean = this.pages > VISIBLE_DOTS;
    const moveLeft: boolean = this.isMovingLeft && hasMoreThanVisibleDots && isInnerDots;
    const moveRight: boolean = this.isMovingRight && hasMoreThanVisibleDots && isInnerDots;

    return (
      <Host //
        role="tablist"
        aria-label={this.dotNavigationAriaLabel}
        class={{ 'wb-dot-navigation': true, 'initial-pages': isInitialPages, 'last-pages': isLastPages, 'move-left': moveLeft, 'move-right': moveRight }}
        data-on={getDataOnFromTheme(this.el, this.theme)}
      >
        <span class="container">
          <span class="dots">
            {[...Array(this.pages)].map((_, key) => (
              <DotElement key={key + 1} transitionPage={this.transitionPage} pages={this.pages} dotElementAriaLabel={this.dotElementAriaLabel} />
            ))}
          </span>
        </span>
      </Host>
    );
  }
}
